import type {
  Repository,
  Contribution,
  ContributionType,
} from "@/data/contribution-types";
import { CONTRIBUTION_TYPES } from "@/data/contribution-types";
import { trimSearchQuery } from "@/util/search-query";

/**
 * Sort options for contributions
 */
export const SortOption = {
  Stars: "stars",
  Date: "date",
  Repository: "repository",
  Technology: "technology",
} as const;
export type SortOption = (typeof SortOption)[keyof typeof SortOption];

export const SORT_OPTIONS = Object.values(SortOption) as SortOption[];

/**
 * Human-readable labels for sort options
 */
export const SORT_LABELS: Record<SortOption, string> = {
  [SortOption.Stars]: "Stars",
  [SortOption.Date]: "Date",
  [SortOption.Repository]: "Repository",
  [SortOption.Technology]: "Technology",
};

/**
 * Represents a flattened contribution row for display
 */
export type ContributionRow = {
  repositoryId: string;
  repositoryName: string;
  repositoryUrl: string;
  stars: number;
  technologies: string[];
  contributionYears: number[];
  contribution: Contribution;
  /** The maximum year for this specific contribution (most recent) */
  maxYear: number;
  /** Related repository ID if this repo has one */
  relatedRepositoryId?: string;
};

/**
 * A group of related contributions (e.g., home-assistant core + docs)
 */
export type ContributionGroup = {
  /** Primary repository (the one with more stars or first in sort order) */
  primaryRow: ContributionRow;
  /** Related contributions from other repos */
  relatedRows: ContributionRow[];
  /** All rows in the group for iteration */
  allRows: ContributionRow[];
  /** Maximum stars in the group (for sorting) */
  maxStars: number;
  /** Most recent year in the group (for sorting) */
  maxYear: number;
};

/**
 * Extracts searchable text parts from contribution data.
 * Centralizes the definition of what content is indexed for search.
 */
function extractSearchableParts(
  repositoryName: string,
  technologies: string[],
  contribution: Contribution,
): string[] {
  return [
    ...extractVisibleSearchableParts(repositoryName, technologies),
    ...extractHiddenSearchableParts(contribution),
  ];
}

function extractVisibleSearchableParts(
  repositoryName: string,
  technologies: string[],
): string[] {
  return [repositoryName, ...technologies];
}

function extractHiddenSearchableParts(contribution: Contribution): string[] {
  return [
    contribution.description,
    ...contribution.types,
    ...contribution.commits.map((commit) => commit.title),
  ];
}

/**
 * Builds flat contribution rows from repositories
 */
export function buildContributionRows(
  repositories: Repository[],
): ContributionRow[] {
  const rows: ContributionRow[] = [];

  for (const repo of repositories) {
    for (const contribution of repo.contributions) {
      // The search text is derived on the client when a query arrives; shipping
      // it precomputed added 35 KB to the page's serialized props for 109 rows.
      rows.push({
        repositoryId: repo.id,
        repositoryName: repo.name,
        repositoryUrl: repo.url,
        stars: repo.stars,
        technologies: repo.technologies,
        contributionYears: repo.contributionYears,
        contribution,
        maxYear:
          repo.contributionYears.length > 0
            ? Math.max(...repo.contributionYears)
            : 0,
        relatedRepositoryId: repo.relatedRepositoryId,
      });
    }
  }

  return rows;
}

/**
 * Groups related contributions together (e.g., home-assistant core and docs)
 */
export function groupContributions(
  rows: ContributionRow[],
): ContributionGroup[] {
  // Build a map of repo ID to all its rows
  const rowsByRepo = Map.groupBy(rows, (row) => row.repositoryId);

  // Find repos that have related repos and group them
  const processedRepos = new Set<string>();
  const groups: ContributionGroup[] = [];

  for (const row of rows) {
    if (processedRepos.has(row.repositoryId)) continue;

    const repoRows = rowsByRepo.get(row.repositoryId) ?? [];
    let relatedRows: ContributionRow[] = [];

    // Check if this repo has a related repo
    if (row.relatedRepositoryId) {
      relatedRows = rowsByRepo.get(row.relatedRepositoryId) ?? [];
      processedRepos.add(row.relatedRepositoryId);
    }

    // Check if another repo points to this one
    for (const [repoId, otherRows] of rowsByRepo) {
      if (repoId === row.repositoryId) continue;
      if (processedRepos.has(repoId)) continue;

      const firstOther = otherRows[0];
      if (firstOther?.relatedRepositoryId === row.repositoryId) {
        relatedRows = [...relatedRows, ...otherRows];
        processedRepos.add(repoId);
      }
    }

    processedRepos.add(row.repositoryId);

    // Determine primary (highest stars) vs related
    const allRows = [...repoRows, ...relatedRows];
    const group = createContributionGroup(allRows);
    if (group) {
      groups.push(group);
    }
  }

  return groups;
}

/**
 * Sort direction
 */
export type SortDirection = "asc" | "desc";

/**
 * Gets the default sort direction for a given sort option
 * Numeric sorts (stars, date) default to descending (highest/newest first)
 * Alphabetical sorts (repository, technology) default to ascending (A-Z)
 */
export function getDefaultSortDirection(sortBy: SortOption): SortDirection {
  switch (sortBy) {
    case SortOption.Stars:
    case SortOption.Date:
      return "desc";
    case SortOption.Repository:
    case SortOption.Technology:
      return "asc";
    default:
      return "desc";
  }
}

const SORT_COMPARATORS: Record<
  SortOption,
  (a: ContributionGroup, b: ContributionGroup) => number
> = {
  [SortOption.Stars]: (a, b) => a.maxStars - b.maxStars,
  [SortOption.Date]: (a, b) => a.maxYear - b.maxYear,
  [SortOption.Repository]: (a, b) =>
    a.primaryRow.repositoryName
      .toLowerCase()
      .localeCompare(b.primaryRow.repositoryName.toLowerCase()),
  [SortOption.Technology]: (a, b) => {
    const techA = a.primaryRow.technologies[0]?.toLowerCase() ?? "";
    const techB = b.primaryRow.technologies[0]?.toLowerCase() ?? "";
    return techA.localeCompare(techB);
  },
};

/**
 * Sorts contribution groups by the specified option
 */
export function sortContributionGroups(
  groups: ContributionGroup[],
  sortBy: SortOption,
  direction?: SortDirection,
): ContributionGroup[] {
  // Shallow copy groups to prevent mutation of the original array
  const sorted = [...groups];
  const effectiveDirection = direction ?? getDefaultSortDirection(sortBy);
  const comparator = SORT_COMPARATORS[sortBy];

  const modifier = effectiveDirection === "asc" ? 1 : -1;
  sorted.sort((a, b) => comparator(a, b) * modifier);

  return sorted;
}

/**
 * Filters contribution groups by search query, type, and technology.
 * When type/tech filters are applied, rows within groups are also filtered
 * to ensure counts and displayed contributions are consistent with the filters.
 */
export function filterContributionGroups(
  groups: ContributionGroup[],
  query: string,
  typeFilter?: ContributionType | null,
  techFilter?: string | null,
): ContributionGroup[] {
  let filtered = groups;

  // Apply type filter - filter both groups AND rows within groups
  if (typeFilter) {
    filtered = filterGroupsByRowPredicate(filtered, (row) =>
      row.contribution.types.includes(typeFilter),
    );
  }

  // Apply technology filter - filter both groups AND rows within groups
  if (techFilter) {
    filtered = filterGroupsByRowPredicate(filtered, (row) =>
      row.technologies.includes(techFilter),
    );
  }

  // Then filter by search query (keeps all matching rows, just filters groups)
  const normalizedQuery = trimSearchQuery(query).toLowerCase();

  if (!normalizedQuery) return filtered;

  return filterGroupsByRowPredicate(filtered, (row) =>
    contributionRowMatchesQuery(row, normalizedQuery),
  );
}

/**
 * Helper to filter groups based on a predicate applied to their rows.
 * If any row in a group matches the predicate, the group is preserved (rebuilt with matching rows).
 * If no rows match, the group is discarded.
 */
function filterGroupsByRowPredicate(
  groups: ContributionGroup[],
  predicate: (row: ContributionRow) => boolean,
): ContributionGroup[] {
  return groups
    .map((group) => {
      const filteredRows = group.allRows.filter(predicate);

      // Optimization: if no rows were filtered out, reuse the existing group object
      // to preserve reference equality and allow React.memo to work
      if (filteredRows.length === group.allRows.length) {
        return group;
      }

      // If we filtered rows, they are still sorted by stars (stable filter).
      // So we can skip re-sorting.
      return createContributionGroup(filteredRows, true);
    })
    .filter((group): group is ContributionGroup => group !== null);
}

/**
 * Creates a ContributionGroup from a list of rows.
 * Sorts rows by stars to determine the primary row.
 * Returns null if the list is empty.
 */
function createContributionGroup(
  rows: ContributionRow[],
  isSortedByStars = false,
): ContributionGroup | null {
  if (rows.length === 0) return null;

  // Sort by stars to determine primary
  const sortedRows = isSortedByStars
    ? rows
    : [...rows].sort((a, b) => b.stars - a.stars);
  const primaryRow = sortedRows[0]!;
  const relatedRows = sortedRows.slice(1);

  return {
    primaryRow,
    relatedRows,
    allRows: sortedRows,
    maxStars: Math.max(...sortedRows.map((row) => row.stars)),
    maxYear: Math.max(...sortedRows.map((row) => row.maxYear)),
  };
}

/**
 * Gets all searchable text parts from a contribution row.
 */
function getContributionSearchableParts(row: ContributionRow): string[] {
  return extractSearchableParts(
    row.repositoryName,
    row.technologies,
    row.contribution,
  );
}

/**
 * Checks if a contribution row matches a search query.
 */
function contributionRowMatchesQuery(
  row: ContributionRow,
  normalizedQuery: string,
): boolean {
  return getContributionSearchableParts(row).some((part) =>
    part.toLowerCase().includes(normalizedQuery),
  );
}

/**
 * Returns whether a group matches a query exclusively through fields hidden
 * behind its disclosure.
 */
export function contributionGroupMatchesOnlyHiddenText(
  group: ContributionGroup,
  query: string,
): boolean {
  const normalizedQuery = trimSearchQuery(query).toLowerCase();
  if (!normalizedQuery) return false;

  const visibleMatch = extractVisibleSearchableParts(
    group.primaryRow.repositoryName,
    group.primaryRow.technologies,
  ).some((part) => part.toLowerCase().includes(normalizedQuery));
  if (visibleMatch) return false;

  return group.allRows.some((row) =>
    getContributionSearchableParts(row).some((part) =>
      part.toLowerCase().includes(normalizedQuery),
    ),
  );
}

/**
 * Statistics about contribution types
 */
export type ContributionTypeStats = {
  type: ContributionType;
  count: number;
  percentage: number;
};

/**
 * Calculates statistics about contribution types across all contributions
 */
export function calculateContributionTypeStats(
  rows: ContributionRow[],
): ContributionTypeStats[] {
  // Count contributions by type (a contribution can have multiple types)
  const typeCounts = new Map<ContributionType, number>();

  for (const row of rows) {
    for (const type of row.contribution.types) {
      typeCounts.set(type, (typeCounts.get(type) ?? 0) + 1);
    }
  }

  // Calculate total (sum of all type counts, not unique contributions)
  const total = Array.from(typeCounts.values()).reduce(
    (sum, count) => sum + count,
    0,
  );

  // Build stats array with all types (even ones with 0 count)
  const stats: ContributionTypeStats[] = CONTRIBUTION_TYPES.map((type) => ({
    type,
    count: typeCounts.get(type) ?? 0,
    percentage: total > 0 ? ((typeCounts.get(type) ?? 0) / total) * 100 : 0,
  }));

  // Sort by count descending
  stats.sort((a, b) => b.count - a.count);

  return stats;
}

/**
 * Summary statistics for contributions
 */
export type ContributionSummary = {
  totalRepositories: number;
  totalContributions: number;
  totalCommits: number;
  totalStars: number;
  yearRange: { min: number; max: number };
  uniqueTechnologies: string[];
};

/**
 * Calculates summary statistics for contributions
 */
export function calculateContributionSummary(
  repositories: Repository[],
): ContributionSummary {
  let totalContributions = 0;
  let totalCommits = 0;
  let totalStars = 0;
  let minYear = Infinity;
  let maxYear = -Infinity;

  for (const repo of repositories) {
    totalStars += repo.stars;

    for (const year of repo.contributionYears) {
      if (year < minYear) minYear = year;
      if (year > maxYear) maxYear = year;
    }

    for (const contribution of repo.contributions) {
      totalContributions++;
      totalCommits += contribution.commits.length;
    }
  }

  return {
    totalRepositories: repositories.length,
    totalContributions,
    totalCommits,
    totalStars,
    yearRange: {
      min: minYear === Infinity ? 0 : minYear,
      max: maxYear === -Infinity ? 0 : maxYear,
    },
    uniqueTechnologies: getUniqueTechnologies(repositories),
  };
}

/**
 * Builds search haystack for fuzzy matching
 */
export function buildContributionsHay(rows: ContributionRow[]): string[] {
  return rows.map((row) =>
    getContributionSearchableParts(row).join(" ").toLowerCase(),
  );
}

/**
 * Get all unique technologies from repositories
 */
export function getUniqueTechnologies(repositories: Repository[]): string[] {
  const technologies = new Set<string>();
  for (const repo of repositories) {
    for (const tech of repo.technologies) {
      technologies.add(tech);
    }
  }
  return Array.from(technologies).sort();
}

/**
 * Statistics about technologies used in contributions
 */
export type TechnologyStats = {
  technology: string;
  repoCount: number;
  percentage: number;
};

/**
 * Calculates statistics about technologies across all repositories
 */
export function calculateTechnologyStats(
  repositories: Repository[],
): TechnologyStats[] {
  const techCounts = new Map<string, number>();

  for (const repo of repositories) {
    for (const tech of repo.technologies) {
      techCounts.set(tech, (techCounts.get(tech) ?? 0) + 1);
    }
  }

  const total = repositories.length;

  const stats: TechnologyStats[] = Array.from(techCounts.entries()).map(
    ([technology, repoCount]) => ({
      technology,
      repoCount,
      percentage: total > 0 ? (repoCount / total) * 100 : 0,
    }),
  );

  // Sort by count descending
  stats.sort((a, b) => b.repoCount - a.repoCount);

  return stats;
}

function addCommitsSpreadAcrossYears(
  commitsByYear: Map<number, number>,
  sortedUniqueYears: number[],
  commitCount: number,
  startOffset: number = 0,
): void {
  if (commitCount <= 0) return;
  if (sortedUniqueYears.length === 0) return;

  const yearCount = sortedUniqueYears.length;
  const basePerYear = Math.floor(commitCount / yearCount);
  const extraYears = commitCount % yearCount; // how many years get +1

  for (let i = 0; i < yearCount; i++) {
    const year = sortedUniqueYears[i]!;

    // We want to distribute the 'extra' commits starting from 'startOffset'.
    // Check if the current index 'i' is in the range [startOffset, startOffset + extraYears)
    // in a circular manner using modulo arithmetic to handle wrap-around.
    const isExtra = (i - startOffset + yearCount) % yearCount < extraYears;

    const add = basePerYear + (isExtra ? 1 : 0);
    if (add === 0) continue; // do not insert 0 entries

    commitsByYear.set(year, (commitsByYear.get(year) ?? 0) + add);
  }
}

/**
 * Calculates the number of commits per year from repositories
 * @returns Array of [year, count] tuples sorted by year ascending
 */
export function calculateCommitsByYear(
  repositories: Repository[],
): [number, number][] {
  const commitsByYear = new Map<number, number>();

  for (const repo of repositories) {
    // Deterministic + defensive: no duplicates, predictable order
    // Pre-calculated once per repo to avoid repeated Set creation/sorting in the loop
    const sortedUniqueYears = Array.from(new Set(repo.contributionYears)).sort(
      (a, b) => a - b,
    );

    let runningOffset = 0;

    // We use contribution years as proxy - count commits for each year the repo was contributed to
    // For more accurate data, we'd need to parse commit dates
    for (const contribution of repo.contributions) {
      addCommitsSpreadAcrossYears(
        commitsByYear,
        sortedUniqueYears,
        contribution.commits.length,
        runningOffset,
      );

      // Advance the offset by the remainder so the next contribution continues distribution
      if (sortedUniqueYears.length > 0) {
        runningOffset =
          (runningOffset +
            (contribution.commits.length % sortedUniqueYears.length)) %
          sortedUniqueYears.length;
      }
    }
  }

  // Convert to sorted array of tuples
  return Array.from(commitsByYear.entries()).sort(
    ([yearA], [yearB]) => yearA - yearB,
  );
}

/**
 * Calculates the total number of commits across all rows in a ContributionGroup.
 */
export function countCommitsInGroup(group: ContributionGroup): number {
  let count = 0;
  for (const row of group.allRows) {
    count += row.contribution.commits.length;
  }
  return count;
}

/**
 * Counts the contributions in a ContributionGroup.
 *
 * Each row carries exactly one contribution, so this is the number of separate
 * pieces of work in the group, where `countCommitsInGroup` counts the commits
 * those pieces of work took. A single contribution can span several commits, so
 * the two numbers differ and are not interchangeable.
 */
export function countContributionsInGroup(group: ContributionGroup): number {
  return group.allRows.length;
}

/**
 * Extracts a sorted array of unique years across all rows in a ContributionGroup.
 */
export function getUniqueYearsInGroup(group: ContributionGroup): number[] {
  const years = new Set<number>();
  for (const row of group.allRows) {
    for (const year of row.contributionYears) {
      years.add(year);
    }
  }
  return Array.from(years).sort((a, b) => a - b);
}

/**
 * Stable DOM id for a group's expandable details panel, used by `aria-controls`
 * on the disclosure button. Repository ids contain slashes, which are not valid
 * in an id, so non-id characters collapse to hyphens.
 *
 * Each group renders once, so the repository id alone identifies its panel.
 */
export function getSafeRepositoryId(id: string) {
  return `repo-details-${id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}
